import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  AppAuthValueProvider,
  createGuestAuthValue,
  useSyntheticProfileController,
} from "~/lib/auth/app-auth";
import { m } from "~/lib/i18n";
import { PrivyLoginBootstrapContext } from "./privy-login-bootstrap-context";

interface PrivyAppRuntimeProviderProps {
  children: ReactNode;
  onPendingLoginHandled?: () => void;
  pendingLogin?: boolean;
}

interface LazyPrivyAppProviderProps {
  children: ReactNode;
  mountRuntimeAfterPublicBootstrap?: boolean;
  pathname: string;
  publicBootstrap?: boolean;
  /** Retain public content while loading, but fail through the reload boundary. */
  runtimeRequired?: boolean;
}

type PrivyAppRuntimeProviderComponent = (
  props: PrivyAppRuntimeProviderProps,
) => ReactNode;
type PrivyAppRuntimeProviderModule = {
  PrivyAppRuntimeProvider: PrivyAppRuntimeProviderComponent;
};
type PrivyAppRuntimeProviderLoader =
  () => Promise<PrivyAppRuntimeProviderModule>;

const loadPrivyAppRuntimeProviderModuleDefault: PrivyAppRuntimeProviderLoader =
  async () => await import("./privy-app-runtime-provider");

const subscribeToHydration = () => () => {};
const getHydratedSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

let cachedPrivyAppRuntimeProvider: PrivyAppRuntimeProviderComponent | null =
  null;
let cachedPrivyAppRuntimeProviderPromise: Promise<PrivyAppRuntimeProviderComponent> | null =
  null;
let loadPrivyAppRuntimeProviderModule =
  loadPrivyAppRuntimeProviderModuleDefault;

export function preloadPrivyAppRuntimeProvider(): Promise<PrivyAppRuntimeProviderComponent> {
  if (cachedPrivyAppRuntimeProvider) {
    return Promise.resolve(cachedPrivyAppRuntimeProvider);
  }

  if (!cachedPrivyAppRuntimeProviderPromise) {
    cachedPrivyAppRuntimeProviderPromise = loadPrivyAppRuntimeProviderModule()
      .then((module) => {
        cachedPrivyAppRuntimeProvider = module.PrivyAppRuntimeProvider;
        return module.PrivyAppRuntimeProvider;
      })
      .catch((error: unknown) => {
        cachedPrivyAppRuntimeProviderPromise = null;
        throw error;
      });
  }

  return cachedPrivyAppRuntimeProviderPromise;
}

export function __setPrivyAppRuntimeProviderLoaderForTests(
  loader: PrivyAppRuntimeProviderLoader,
): void {
  loadPrivyAppRuntimeProviderModule = loader;
  cachedPrivyAppRuntimeProvider = null;
  cachedPrivyAppRuntimeProviderPromise = null;
}

export function __resetLazyPrivyAppProviderForTests(): void {
  loadPrivyAppRuntimeProviderModule = loadPrivyAppRuntimeProviderModuleDefault;
  cachedPrivyAppRuntimeProvider = null;
  cachedPrivyAppRuntimeProviderPromise = null;
}

function PrivyAppRuntimeFallbackShell() {
  return (
    <div
      aria-busy="true"
      className="min-h-screen bg-background text-foreground"
      data-testid="lazy-privy-runtime-fallback"
      role="status"
    >
      <span className="sr-only">
        {m.components_lazy_privy_app_provider_loading_label()}
      </span>
    </div>
  );
}

export function LazyPrivyAppProvider(props: LazyPrivyAppProviderProps) {
  const [customLoginRequested, setCustomLoginRequested] = useState(false);
  const requestCustomLogin = useCallback(
    () => setCustomLoginRequested(true),
    [],
  );
  const clearCustomLogin = useCallback(
    () => setCustomLoginRequested(false),
    [],
  );
  const bootstrap = useMemo(
    () => ({ customLoginRequested, requestCustomLogin, clearCustomLogin }),
    [customLoginRequested, requestCustomLogin, clearCustomLogin],
  );
  return (
    <PrivyLoginBootstrapContext value={bootstrap}>
      <LazyPrivyAppProviderContent
        {...props}
        customLoginRequested={customLoginRequested}
        onCustomLoginFailed={clearCustomLogin}
      />
    </PrivyLoginBootstrapContext>
  );
}

function LazyPrivyAppProviderContent(
  props: LazyPrivyAppProviderProps & {
    customLoginRequested: boolean;
    onCustomLoginFailed: () => void;
  },
) {
  const controller = useSyntheticProfileController();
  const hasHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydratedSnapshot,
    getServerHydrationSnapshot,
  );
  const mountRuntimeAfterPublicBootstrap =
    props.mountRuntimeAfterPublicBootstrap ?? false;
  const publicBootstrap = props.publicBootstrap ?? false;
  const runtimeRequired = props.runtimeRequired ?? false;
  const [RuntimeProvider, setRuntimeProvider] =
    useState<PrivyAppRuntimeProviderComponent | null>(
      // Module preloads may finish before hydration. Initial markup must not
      // depend on that race: mount the cached runtime from the effect instead.
      null,
    );
  const [bootstrapError, setBootstrapError] = useState<Error | null>(null);
  const [optionalRuntimeFailed, setOptionalRuntimeFailed] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);
  const [shouldUsePrivyRuntime, setShouldUsePrivyRuntime] = useState(
    () => !publicBootstrap || runtimeRequired,
  );

  const recoverGuestRuntime = useCallback(() => {
    setPendingLogin(false);
    props.onCustomLoginFailed();
    if (publicBootstrap) {
      setOptionalRuntimeFailed(true);
      setShouldUsePrivyRuntime(false);
    }
  }, [publicBootstrap, props.onCustomLoginFailed]);

  useEffect(() => {
    if (!publicBootstrap || props.customLoginRequested || runtimeRequired) {
      setShouldUsePrivyRuntime(true);
    }
  }, [publicBootstrap, props.customLoginRequested, runtimeRequired]);

  useEffect(() => {
    if (
      !publicBootstrap ||
      !mountRuntimeAfterPublicBootstrap ||
      runtimeRequired ||
      shouldUsePrivyRuntime ||
      typeof window === "undefined"
    ) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | null = null;
    let frameId: number | null = null;
    const preloadAfterPaint = () => {
      timeoutId = window.setTimeout(() => {
        if (cancelled) {
          return;
        }

        void preloadPrivyAppRuntimeProvider()
          .then((loadedProvider) => {
            if (cancelled) {
              return;
            }

            if (mountRuntimeAfterPublicBootstrap) {
              setRuntimeProvider(() => loadedProvider);
              setShouldUsePrivyRuntime(true);
            }
          })
          .catch(() => {
            if (!cancelled) setOptionalRuntimeFailed(true);
          });
      }, 0);
    };

    if (typeof window.requestAnimationFrame === "function") {
      frameId = window.requestAnimationFrame(preloadAfterPaint);
    } else {
      preloadAfterPaint();
    }

    return () => {
      cancelled = true;
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    mountRuntimeAfterPublicBootstrap,
    publicBootstrap,
    runtimeRequired,
    shouldUsePrivyRuntime,
  ]);

  useEffect(() => {
    if (RuntimeProvider || !shouldUsePrivyRuntime) {
      return;
    }

    let cancelled = false;
    void preloadPrivyAppRuntimeProvider()
      .then((loadedProvider) => {
        if (!cancelled) {
          setRuntimeProvider(() => loadedProvider);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          if (!publicBootstrap || runtimeRequired) {
            // A required chunk failed, rather than merely loading slowly.
            // Let the root boundary commit its existing reload screen.
            setBootstrapError(
              error instanceof Error ? error : new Error(String(error)),
            );
          }
          recoverGuestRuntime();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    RuntimeProvider,
    publicBootstrap,
    recoverGuestRuntime,
    runtimeRequired,
    shouldUsePrivyRuntime,
  ]);

  const requestPrivyLogin = useCallback(async () => {
    setPendingLogin(true);
    setShouldUsePrivyRuntime(true);
    try {
      const loadedProvider = await preloadPrivyAppRuntimeProvider();
      setRuntimeProvider(() => loadedProvider);
    } catch (error) {
      recoverGuestRuntime();
      throw error;
    }
  }, [recoverGuestRuntime]);

  const guestValue = useMemo(
    () => ({
      ...createGuestAuthValue(controller),
      login: requestPrivyLogin,
      ready:
        hasHydrated &&
        !pendingLogin &&
        !props.customLoginRequested &&
        !runtimeRequired &&
        (!mountRuntimeAfterPublicBootstrap || optionalRuntimeFailed),
    }),
    [
      controller,
      hasHydrated,
      pendingLogin,
      props.customLoginRequested,
      mountRuntimeAfterPublicBootstrap,
      optionalRuntimeFailed,
      requestPrivyLogin,
      runtimeRequired,
    ],
  );

  if (RuntimeProvider && shouldUsePrivyRuntime) {
    return (
      <RuntimeProvider
        onPendingLoginHandled={() => {
          setPendingLogin(false);
        }}
        pendingLogin={pendingLogin}
      >
        {props.children}
      </RuntimeProvider>
    );
  }

  if (bootstrapError) {
    throw bootstrapError;
  }

  if (publicBootstrap && (!RuntimeProvider || !shouldUsePrivyRuntime)) {
    return (
      <AppAuthValueProvider value={guestValue}>
        {props.children}
      </AppAuthValueProvider>
    );
  }

  return <PrivyAppRuntimeFallbackShell />;
}
